import { axiosMyInfo, axiosServicesV1, axiosServicesV2, axiosServicesV3 } from 'utils/services';
import { endpoint } from 'common/config';
import type { CreateAppValues, Attachment, AttachPayload } from 'types/application';
import { decryptId } from 'common/decrypt';

export interface CreateAmendValues extends CreateAppValues {
    licenceKey: string;
    amendType: 'Material' | 'Non-Material' | 'Both';
}

export interface CreateRenewValues extends CreateAppValues {
    licenceKey: string;
}

export const requestCreate = async (values: CreateAppValues) => {
    try {
        // Inject AppSource at creation time for ROE (and generally licence applications) so backend has the source from the start.
        // Previously this was only added during save transformation (roe.formatSave). Requirement: present on creation payload as well.
        const isLicenceApp = values.application === 'IMDA-IRIS-Work-LICApplication';
        const isROE = isLicenceApp && values.applicationType === 'Radio Operator Exam';
        const isACA = isLicenceApp && values.applicationType === 'Accreditation of Certification Authority';
        const isAA = isLicenceApp && values.applicationType === 'Registration of Accounting Authority';
        const content = isLicenceApp
            ? ({
                  licenceName: values.applicationType,
                  ApplicationForm: values.applicationForm,
                  // ROE requires these meta fields to be present from creation (previously only added on save)
                  ...(isROE && {
                      AppSource: 'FEP',
                      LicenceCategory: "General Operator's Certificate (GOC)",
                      transactionType: 'New',
                      Type: 'Individual'
                  }),

                  ...(isACA && {
                      AppSource: 'FEP',
                      transactionType: 'New'
                  }),

                  ...(isAA && {
                      AppSource: 'FEP',
                      transactionType: 'New',
                  })
              } as const)
            : ({
                  ApplicationType: values.applicationType,
                  ApplicationForm: values.applicationForm
              } as const);

        const payload = {
            caseTypeID: values.application,
            content
        };

        const response = await axiosServicesV1.post(endpoint.CASES, payload);
        return response;
    } catch (error) {
        console.error(error);
        return { data: {}, headers: {} };
    }
};

export const requestCreateAmend = async (values: CreateAmendValues) => {
    try {
        // Amend uses the generic LICApplication caseType, same as new applications
        const isLicenceApp = values.application === 'IMDA-IRIS-Work-LICApplication';
        const isACA = isLicenceApp && values.applicationType === 'Accreditation of Certification Authority';

        if (!isACA) {
            throw new Error('Amend is only supported for ACA applications');
        }

        // First, fetch the original licence data to get required identifiers
        const licenceData = await requestDetails(values.licenceKey);
        const originalContent = licenceData.content || {};
        const originalLicApp = originalContent.LICApplication || {};
        const originalLicence = originalLicApp.licence || {};

        // Only include selected fields in iWantToUpdate array
        const iWantToUpdate = [];
        if (values.amendType === 'Material' || values.amendType === 'Both') {
            iWantToUpdate.push({ mixedValue: 'Material Fields' });
        }
        if (values.amendType === 'Non-Material' || values.amendType === 'Both') {
            iWantToUpdate.push({ mixedValue: 'Non Material Fields' });
        }

        // Copy entire parent objects from original licence data
        const content = {
            AppSource: 'FEP',
            licenceName: values.applicationType,
            IDNoRef: originalContent.IDNoRef || originalContent.UEN,
            IDNo: originalContent.IDNo,
            UEN: originalContent.UEN,
            transactionType: 'Amend',
            licenceNumber: originalContent.licenceNumber,
            pyWorkIDPrefix: 'LI-',
            LICApplication: {
                // Copy the entire LICApplication from original
                ...originalLicApp,
                pxObjClass: 'IMDA-IRIS-Data-Licence-Application',
                licence: {
                    // Copy all fields from original licence
                    ...originalLicence,
                    // Add iWantToUpdate for amend
                    licenceDetails: {
                        iWantToUpdate
                    }
                }
            }
        };

        const payload = {
            caseTypeID: values.application, // Use generic IMDA-IRIS-Work-LICApplication
            content
        };

        const response = await axiosServicesV1.post(endpoint.CASES, payload);
        return response;
    } catch (error) {
        console.error(error);
        return { data: {}, headers: {} };
    }
};

export const requestCreateRenew = async (values: CreateRenewValues) => {
    try {
        const isACA = values.applicationType === 'Accreditation of Certification Authority';

        if (!isACA) {
            throw new Error('Renew is only supported for ACA applications');
        }

        // Step 1: Fetch the original licence data
        const licenceData = await requestDetails(values.licenceKey);

        // Step 2: Check expiry date to determine if payment is required
        const licenceExpiryDate = licenceData.content?.ExpirationDate;

        let makePayment = false;

        if (licenceExpiryDate) {
            const expiryDate = new Date(licenceExpiryDate);
            const currentDate = new Date();

            // Check if licence has already expired (past expiry date)
            if (currentDate > expiryDate) {
                throw new Error(
                    `This licence expired on ${expiryDate.toLocaleDateString()} and can no longer be renewed. Please contact IMDA for assistance.`
                );
            }

            // Calculate 3 months before expiry date
            const threeMonthsBeforeExpiry = new Date(expiryDate);
            threeMonthsBeforeExpiry.setMonth(expiryDate.getMonth() - 3);

            // If current date is AFTER the 3-month deadline, payment is required
            // Example: Licence expires 25 March 2027 → deadline is 25 December 2026
            // If submitting after 25 December 2026, payment required
            makePayment = currentDate > threeMonthsBeforeExpiry;
        }

        // Step 3: Merge the original licence data with renew metadata
        const originalContent = licenceData.content || {};
        const originalLicApp = originalContent.LICApplication || {};
        const originalLicence = originalLicApp.licence || {};

        // Copy entire parent objects from original licence data (similar to amend, but without iWantToUpdate)
        const content = {
            AppSource: 'FEP',
            licenceName: values.applicationType,
            IDNoRef: originalContent.IDNoRef || originalContent.UEN,
            IDNo: originalContent.IDNo,
            UEN: originalContent.UEN,
            transactionType: 'Renew',
            licenceNumber: originalContent.licenceNumber,
            LICApplication: {
                // Copy the entire LICApplication from original
                ...originalLicApp,
                pxObjClass: 'IMDA-IRIS-Data-Licence-Application',
                licence: {
                    // Copy all fields from original licence
                    ...originalLicence
                    // No iWantToUpdate for renew - all fields are shown
                }
            }
        };

        const payload = {
            caseTypeID: 'IMDA-IRIS-Work-LICApplication',
            content
        };

        const response = await axiosServicesV1.post(endpoint.CASES, payload);

        // Return response with payment requirement info for configuration selection
        if (response?.data?.ID) {
            return {
                ...response,
                makePayment
            };
        }

        return response;
    } catch (error) {
        console.error('Error creating renew application:', error);
        // Re-throw the error so it can be caught by the Redux thunk
        throw error;
    }
};

export const requestCreateCancel = async (values: {
    application: string;
    applicationType: string;
    applicationForm: string;
    licenceKey: string;
}) => {
    try {
        // Cancel uses the generic LICApplication caseType, same as new applications
        const isLicenceApp = values.application === 'IMDA-IRIS-Work-LICApplication';
        const isACA = isLicenceApp && values.applicationType === 'Accreditation of Certification Authority';
        const isAA = isLicenceApp && values.applicationType === 'Registration of Accounting Authority';

        if (!isACA && !isAA) {
            throw new Error('Cancel is only supported for ACA and AA applications');
        }

        // First, fetch the original licence data to get required identifiers
        const licenceData = await requestDetails(values.licenceKey);
        const originalContent = licenceData.content || {};
        const originalLicApp = originalContent.LICApplication || {};
        const originalLicence = originalLicApp.licence || {};

        // Copy entire parent objects from original licence data (similar to amend/renew)
        const content = {
            AppSource: 'FEP',
            licenceName: values.applicationType,
            ApplicationForm: values.applicationForm,
            IDNoRef: originalContent.IDNoRef || originalContent.UEN,
            IDNo: originalContent.IDNo,
            UEN: originalContent.UEN,
            transactionType: 'Cancel',
            licenceNumber: originalContent.licenceNumber,
            pyWorkIDPrefix: 'LI-',
            LICApplication: {
                // Copy the entire LICApplication from original
                ...originalLicApp,
                pxObjClass: 'IMDA-IRIS-Data-Licence-Application',
                licence: {
                    // Copy all fields from original licence
                    ...originalLicence,
                    // Add cancellationInformation for cancel (will be filled in the form)
                    cancellationInformation: {
                        pxObjClass: 'IMDA-IRIS-Data-Cancellation'
                    }
                }
            }
        };

        const payload = {
            caseTypeID: values.application, // Use generic IMDA-IRIS-Work-LICApplication
            content
        };

        const response = await axiosServicesV1.post(endpoint.CASES, payload);
        return response;
    } catch (error) {
        console.error(error);
        return { data: {}, headers: {} };
    }
};

export const requestMyInfoMock = async (id: string) => {
    const decrypted = await decryptId(id, 'decrypt');

    try {
        const response = await axiosMyInfo.get(`myinfo-sandbox/?id=${decrypted}`);
        if (response?.data) return response.data;
    } catch (error) {
        console.error(error);
    }

    return {};
};

export const requestDetails = async (caseTypeID: string) => {
    if (caseTypeID === 'F-0001') return { content: {} };
    try {
        const { data: baseData } = await axiosServicesV1.get(`${endpoint.CASES}/${caseTypeID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        const caseID = caseTypeID.split(' ')[1];
        const licenceName = baseData?.content?.licenceName;
        if (licenceName === 'Telecommunication Wiring Contractor Class Licence') {
            const { data: extraData } = await axiosServicesV1.get(`/data_iris/D_GetFEPWiringInstallersByContractorId?CaseID=${caseID}`, {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            });

            const wiringList = baseData?.content?.LICApplication?.licence?.WiringInstallerList ?? [];
            const extraList = extraData?.pxResults ?? [];

            const mergedList = wiringList.map((wi: any) => {
                const matched = extraList.find((extra: any) => extra.WILicenceNo === wi.WILicenceNo);
                return matched ? { ...wi, ...matched } : wi; // merge if found, otherwise keep original
            });

            return {
                ...baseData,
                content: {
                    ...baseData.content,
                    LICApplication: {
                        ...baseData.content.LICApplication,
                        licence: {
                            ...baseData.content.LICApplication.licence,
                            WiringInstallerList: mergedList
                        }
                    }
                }
            };
        }
        return baseData;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestCARegistrationDetails = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV1.get(`${endpoint.DATA}/D_FilmTitlesListByParent?parentKey=${caseTypeID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestCAProfileDetails = async (id: string) => {
    try {
        const decrypted = await decryptId(id || '', 'decrypt');
        const response = await axiosServicesV1.get(`data/D_GetCAProfileForID?IDNo=${decrypted}`);
        return response.data;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestPMNDetails = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV3.get(`api/v2${endpoint.CASES}/${caseTypeID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        return response.data;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestAddress = async (postalCode: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_GetSingPostListByPostalCode?PostalCode=${postalCode}`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestFields = async (assignmentID: string, actionCollect: string) => {
    try {
        const response = await axiosServicesV1.get(`${endpoint.ASSIGNMENTS}/${assignmentID}/actions/${actionCollect}`);
        return response.data;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestSubTypes = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_GetEventSubTypeByType');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestSubLanguageGroup = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_LanguageList');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestLanguageGroupList = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_GetLanguageGroupList');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestLanguageGroupListV2 = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_FVGLanguageGroupList');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestSubLanguageGroupV2 = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_GetLanguageByCategory?Category=SubLanguage&SubCategory');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestLanguageVG = async () => {
    try {
        const response = await axiosServicesV1.get('/data_iris/D_FVGLanguageGroupList?LanguageGroup=Language_VG');
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestDropdownValues = async (id: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/${id}`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestSave = async (payload: any, caseTypeID: string, actionSave?: string) => {
    try {
        const responseGet = await axiosServicesV1.get(`${endpoint.CASES}/${caseTypeID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        const response = await axiosServicesV1.put(
            `${endpoint.CASES}/${caseTypeID}?actionID=${actionSave || 'pyUpdateCaseDetails'}`,
            payload,
            {
                headers: {
                    'If-Match': responseGet.headers.etag,
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        return response;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestSubmitAdditionalInformation = async (payload: any, caseTypeID: string) => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKBASKET ${caseTypeID}!ADDITIONALINFORMATION?actionID=ProcessAdditionalInformation&saveOnly=false`,
            payload,
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );

        return response;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestSubmitKDM = async (payload: any) => {
    try {
        const response = await axiosServicesV1.post(`/update_kdm`, payload);

        return response;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestSubmit = async (caseTypeID: string, action: string) => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKLIST ${caseTypeID}!COLLECTITEM?actionID=${action}&saveOnly=false`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            return { isValid: true };
        }
        return { isValid: false };
    } catch (error: any) {
        console.error(error);
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
            const validationMsgs = error.errors[0].ValidationMessages;
            if (validationMsgs && Array.isArray(validationMsgs)) {
                return {
                    isValid: true,
                    validationMsgs: validationMsgs.map(
                        (validation: { [key: string]: string }) =>
                            `${validation.Path?.replace(/\./g, ' ') || ''}: ${validation.ValidationMessage}`
                    )
                };
            }
        }
        return { isValid: false };
    }
};

export const requestSubmitROE = async (caseTypeID: string, action: string = 'FEPCaptureCaseDetails') => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKLIST ${caseTypeID}!FEPCreateCase?actionID=${action}&saveOnly=false`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            sessionStorage.removeItem('entity-name');
            return { isValid: true };
        }
        return { isValid: false };
    } catch (error: any) {
        console.error(error);
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
            const validationMsgs = error.errors[0].ValidationMessages;
            if (validationMsgs && Array.isArray(validationMsgs)) {
                return {
                    isValid: true,
                    validationMsgs: validationMsgs.map(
                        (validation: { [key: string]: string }) =>
                            `${validation.Path?.replace(/\./g, ' ') || ''}: ${validation.ValidationMessage}`
                    )
                };
            }
        }
        return { isValid: false };
    }
};


export const requestSubmitACA = async (caseTypeID: string, action: string = 'FEPCaptureCaseDetails') => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKLIST ${caseTypeID}!FEPCreateCase?actionID=${action}&saveOnly=false`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            return { isValid: true };
        }
        return { isValid: false };
    } catch (error: any) {
        console.error(error);
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
            const validationMsgs = error.errors[0].ValidationMessages;
            if (validationMsgs && Array.isArray(validationMsgs)) {
                return {
                    isValid: true,
                    validationMsgs: validationMsgs.map(
                        (validation: { [key: string]: string }) =>
                            `${validation.Path?.replace(/\./g, ' ') || ''}: ${validation.ValidationMessage}`
                    )
                };
            }
        }
        return { isValid: false };
    }
};

export const requestSubmitAA = async (caseTypeID: string, action: string = 'FEPCaptureCaseDetails') => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKLIST ${caseTypeID}!FEPCreateCase?actionID=${action}&saveOnly=false`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            return { isValid: true };
        }
        return { isValid: false };
    } catch (error: any) {
        console.error(error);
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
            const validationMsgs = error.errors[0].ValidationMessages;
            if (validationMsgs && Array.isArray(validationMsgs)) {
                return {
                    isValid: true,
                    validationMsgs: validationMsgs.map(
                        (validation: { [key: string]: string }) =>
                            `${validation.Path?.replace(/\./g, ' ') || ''}: ${validation.ValidationMessage}`
                    )
                };
            }
        }
        return { isValid: false };
    }
};

export const requestSubmitFVG = async (caseTypeID: string, action: string) => {
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKBASKET ${caseTypeID}!CAPTUREDATAFROMFEP?actionID=${action}&saveOnly=false`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            return { isValid: true };
        }
        return { isValid: false };
    } catch (error: any) {
        console.error(error);
        if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
            const validationMsgs = error.errors[0].ValidationMessages;
            if (validationMsgs && Array.isArray(validationMsgs)) {
                return {
                    isValid: true,
                    validationMsgs: validationMsgs.map(
                        (validation: { [key: string]: string }) =>
                            `${validation.Path?.replace(/\./g, ' ') || ''}: ${validation.ValidationMessage}`
                    )
                };
            }
        }
        return { isValid: false };
    }
};

export const requestEdit = async (caseTypeID: string) => {
    try {
        const responseV1 = await axiosServicesV1(`${endpoint.CASES}/${caseTypeID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        const response = await axiosServicesV2.patch(
            `${endpoint.ASSIGNMENTS}/ASSIGN-WORKLIST ${caseTypeID}!COLLECTITEM/navigation_steps/previous?viewType=page`,
            {},
            {
                headers: {
                    'If-Match': responseV1.headers.etag,
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        return response;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestUpload = async (attachments: Attachment[], category?: string, caseTypeID?: string) => {
    try {
        const uploadedFiles: AttachPayload[] = await Promise.all(
            attachments.map(async (attachment: Attachment) => {
                const fileName = attachment.file?.name || '';
                const lastDotIndex = fileName.lastIndexOf('.');
                const hasExtension = lastDotIndex !== -1;

                const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
                const extension = hasExtension ? fileName.slice(lastDotIndex + 1) : '';

                const failedUpload = {
                    extension,
                    fileName,
                    mimeType: attachment.file?.type || '',
                    type: 'FILE',
                    name: baseName,
                    category: category || 'File',
                    ID: '',
                    randomValue: attachment.randomValue,
                    error: true,
                    errorType: attachment.errorType || 'failed-upload'
                };

                if (attachment.file) {
                    const payload = new FormData();
                    payload.append('content', attachment.file);

                    try {
                        const response = await axiosServicesV1.post(endpoint.ATTACHMENTS, payload, {
                            headers: {
                                caseid: caseTypeID || '',
                                pzinskey: caseTypeID || ''
                            }
                        });

                        if (response && response.data?.ID) {
                            return {
                                extension,
                                fileName,
                                mimeType: attachment.file.type,
                                type: 'FILE',
                                name: baseName,
                                category: category || 'File',
                                ID: response.data.ID,
                                randomValue: attachment.randomValue
                            };
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }

                return failedUpload;
            })
        );

        return uploadedFiles;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestAttach = async (attachments: Attachment[], caseTypeID: string) => {
    const filesToAttach = attachments.filter((file: Attachment) => !file.error);
    try {
        const response = await axiosServicesV1.post(
            `${endpoint.CASES}/${caseTypeID}/attachments`,
            { attachments: filesToAttach },
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response) {
            return { success: true };
        }
        return { success: false };
    } catch (error) {
        console.error(error);
        return { success: false };
    }
};

export const requestAttachmentList = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV1.get(`${endpoint.CASES}/${caseTypeID}/attachments`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        if (response?.data?.attachments) {
            return response.data.attachments;
        }
        return [];
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestAttachmentFVG = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV3.post(
            `/api/v1/fvgattachment/${caseTypeID}`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response?.data?.AttachmentList) {
            return response.data.AttachmentList;
        }
        return [];
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestAttachmentListLicence = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV3.post(
            `/api/IRISLicence/v1/licenceattachment/${caseTypeID}`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response?.data?.AttachmentList) {
            return response.data.AttachmentList;
        }
        return [];
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestAttachmentListApplication = async (caseTypeID: string) => {
    try {
        const response = await axiosServicesV3.post(
            `/api/IRISLicence/v1/applicationattachment/${caseTypeID}`,
            {},
            {
                headers: {
                    caseid: caseTypeID,
                    pzinskey: caseTypeID
                }
            }
        );
        if (response?.data?.AttachmentList) {
            return response.data.AttachmentList;
        }
        return [];
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestDownloadAttachment = async (fileID: string, caseTypeID?: string) => {
    try {
        const response = await axiosServicesV1.get(`/attachments/${fileID}`, {
            headers: {
                caseid: caseTypeID || '',
                pzinskey: caseTypeID || ''
            }
        });
        if (response) {
            return { success: true, attachment: response.data };
        }
        return { success: false };
    } catch (error) {
        console.error(error);
        return { success: false };
    }
};

export const requestDeleteAttachment = async (fileID: string, caseTypeID: string) => {
    try {
        const response = await axiosServicesV1.delete(`/attachments/${fileID}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        if (response) {
            return { success: true };
        }
        return { success: false };
    } catch (error) {
        console.error(error);
        return { success: false };
    }
};

export const requestMyInfo = async (id: string, code: string, state: string) => {
    // to decrypt user id in local storage
    const decrypted = await decryptId(id, 'decrypt');

    try {
        const response = await axiosServicesV3.post('myinfo/', {
            authCode: code,
            nric: decrypted,
            state
        });
        if (response?.data) return response.data;
    } catch (error) {
        console.error(error);
    }

    return {};
};
export const requestMyInfoBasic = async (id: string) => {
    // to decrypt user id in local storage
    const decrypted = await decryptId(id, 'decrypt');

    try {
        const response = await axiosServicesV3.post('myinfo-basic/', {
            nric: decrypted
        });
        if (response?.data) return response.data;
    } catch (error) {
        console.error(error);
    }

    return {};
};
export const requestMyInfoV2 = async (id: string, code: string) => {
    // to decrypt user id in local storage
    const decrypted = await decryptId(id, 'decrypt');

    try {
        const response = await axiosServicesV3.post('myinfo/', {
            authCode: code,
            sub: decrypted
        });
        if (response?.data) return response.data;
    } catch (error) {
        console.error(error);
    }

    return {};
};

export const requestEDH = async (uen?: string, nric?: string) => {

    if (!uen || !nric) return {};

    // to decrypt user id in local storage
    const decryptedUen = await decryptId(uen || '', 'decrypt');
    const decryptedNric = await decryptId(nric || '', 'decrypt');

    try {
        const response = await axiosServicesV3.post('edh/', { uen: decryptedUen });
        if (response?.data) {
            const addressTemp = response.data.addresses?.find((address: any) => address?.purpose?.value === 'registered');
            const corppassTemp = response.data.corppass?.find((user: any) => user?.['corppass-user']?.['id-no']?.value === decryptedNric);
            const data = {
                'basic-profile': response.data['basic-profile'],
                address: addressTemp,
                corppass: corppassTemp
            };
            return data;
        }
    } catch (error) {
        console.error(error);
    }

    return {};
};

export const requestInvoice = async (caseTypeID?: string, type?: string, pzInsKey?: string) => {
    const failedResult = {
        merchantTxnRef: '',
        netsTxnMsg: 'Failed Transaction.',
        stageRespCode: '',
        netsTxnRef: '0000000000000000',
        actionCode: '',
        netsTxnDtm: '',
        paymentMode: ''
    };

    const caseValue = type === 'SUBMODULE' ? caseTypeID : caseTypeID?.split(' ')[1] || '';
    const pzinsKeyValue = pzInsKey;
    if (!caseValue) return failedResult;

    try {
        const successResponse = await axiosServicesV1.get(`${endpoint.GET_INVOICE_SUCCESS}?AppRefNo=${caseValue}`, {
            headers: {
                caseid: pzinsKeyValue,
                pzinskey: pzinsKeyValue
            }
        });
        const result = successResponse?.data?.pxResults;

        if (result && Array.isArray(result) && result.length) {
            let latestInvoice;

            if (result.length === 1) {
                // If only one invoice, use it directly
                latestInvoice = result[0];
            } else {
                // If multiple invoices, sort by pxCreateDateTime in descending order (latest first)
                const sortedResults = [...result].sort((a, b) => {
                    const dateA = new Date(a.pxCreateDateTime || 0).getTime();
                    const dateB = new Date(b.pxCreateDateTime || 0).getTime();
                    return dateB - dateA; // Descending order (newest first)
                });
                latestInvoice = sortedResults[0];
            }

            try {
                const data = JSON.parse(latestInvoice?.pxPages?.T?.ReturnXML).msg;
                return {
                    merchantTxnRef: data.merchantTxnRef,
                    netsTxnMsg: data.netsTxnMsg,
                    stageRespCode: data.stageRespCode,
                    netsTxnRef: data.netsTxnRef,
                    actionCode: data.actionCode,
                    netsTxnDtm: data.netsTxnDtm,
                    paymentMode: data.paymentMode
                };
            } catch (error) {
                console.error('Error parsing latest invoice ReturnXML:', error);
            }
        } else {
            const errorResponse = await axiosServicesV1.get(`${endpoint.GET_PAYMENT_LOG}?AppRefNo=${caseValue}`, {
                headers: { caseid: pzinsKeyValue, pzinskey: pzinsKeyValue }
            });
            const resultFailed = errorResponse?.data?.pxResults;

            if (resultFailed && Array.isArray(resultFailed) && resultFailed.length) {
                let latestFailedPayment;

                if (resultFailed.length === 1) {
                    // If only one failed payment, use it directly
                    latestFailedPayment = resultFailed[0];
                } else {
                    // If multiple failed payments, sort by creation time
                    const sortedFailedResults = [...resultFailed].sort((a, b) => {
                        const dateA = new Date(a.pxCreateDateTime || 0).getTime();
                        const dateB = new Date(b.pxCreateDateTime || 0).getTime();
                        return dateB - dateA; // Descending order (newest first)
                    });
                    latestFailedPayment = sortedFailedResults[0];
                }

                try {
                    const data = JSON.parse(latestFailedPayment?.ReturnXML).msg;
                    return {
                        merchantTxnRef: data.merchantTxnRef,
                        netsTxnMsg: data.netsTxnMsg,
                        stageRespCode: data.stageRespCode,
                        netsTxnRef: data.netsTxnRef,
                        actionCode: data.actionCode,
                        netsTxnDtm: data.netsTxnDtm,
                        paymentMode: data.paymentMode
                    };
                } catch (error) {
                    console.error('Error parsing latest failed payment ReturnXML:', error);
                }
            }
        }
    } catch (error) {
        console.error(error);
    }

    return failedResult;
};

export const requestInvoiceByAppRefNo = async (appRefNo?: string, parentPzinsKey?: string) => {
    const failedResult = {
        merchantTxnRef: '',
        netsTxnMsg: 'Failed Transaction.',
        stageRespCode: '',
        netsTxnRef: '0000000000000000',
        actionCode: '',
        netsTxnDtm: '',
        paymentMode: '',
        netsAmountDeducted: 0
    };

    try {
        const successResponse = await axiosServicesV1.get(`${endpoint.GET_INVOICE_SUCCESS}?AppRefNo=${appRefNo}`, {
            headers: {
                caseid: parentPzinsKey,
                pzinskey: parentPzinsKey
            }
        });
        const result = successResponse?.data?.pxResults;

        if (result && Array.isArray(result) && result.length) {
            let latestInvoice;

            if (result.length === 1) {
                // If only one invoice, use it directly
                latestInvoice = result[0];
            } else {
                // If multiple invoices, sort by pxCreateDateTime in descending order (latest first)
                const sortedResults = [...result].sort((a, b) => {
                    const dateA = new Date(a.pxCreateDateTime || 0).getTime();
                    const dateB = new Date(b.pxCreateDateTime || 0).getTime();
                    return dateB - dateA; // Descending order (newest first)
                });
                latestInvoice = sortedResults[0];
            }

            try {
                const data = JSON.parse(latestInvoice?.pxPages?.T?.ReturnXML).msg;
                return {
                    merchantTxnRef: data.merchantTxnRef,
                    netsTxnMsg: data.netsTxnMsg,
                    stageRespCode: data.stageRespCode,
                    netsTxnRef: data.netsTxnRef,
                    actionCode: data.actionCode,
                    netsTxnDtm: data.netsTxnDtm,
                    paymentMode: data.paymentMode,
                    netsAmountDeducted: data.netsAmountDeducted
                };
            } catch (error) {
                console.error('Error parsing latest invoice ReturnXML:', error);
            }
        } else {
            const errorResponse = await axiosServicesV1.get(`${endpoint.GET_PAYMENT_LOG}?AppRefNo=${appRefNo}`, {
                headers: { caseid: parentPzinsKey, pzinskey: parentPzinsKey }
            });
            const resultFailed = errorResponse?.data?.pxResults;

            if (resultFailed && Array.isArray(resultFailed) && resultFailed.length) {
                let latestFailedPayment;

                if (resultFailed.length === 1) {
                    // If only one failed payment, use it directly
                    latestFailedPayment = resultFailed[0];
                } else {
                    // If multiple failed payments, sort by creation time
                    const sortedFailedResults = [...resultFailed].sort((a, b) => {
                        const dateA = new Date(a.pxCreateDateTime || 0).getTime();
                        const dateB = new Date(b.pxCreateDateTime || 0).getTime();
                        return dateB - dateA; // Descending order (newest first)
                    });
                    latestFailedPayment = sortedFailedResults[0];
                }

                try {
                    const data = JSON.parse(latestFailedPayment?.ReturnXML).msg;
                    const failedResult = {
                        merchantTxnRef: data.merchantTxnRef,
                        netsTxnMsg: data.netsTxnMsg,
                        stageRespCode: data.stageRespCode,
                        netsTxnRef: data.netsTxnRef,
                        actionCode: data.actionCode,
                        netsTxnDtm: data.netsTxnDtm,
                        paymentMode: data.paymentMode,
                        netsAmountDeducted: data.netsAmountDeducted
                    };

                    return failedResult;
                } catch (error) {
                    console.error('Error parsing latest failed payment ReturnXML:', error);
                }
            }
        }
    } catch (error) {
        console.error(error);
    }

    return failedResult;
};

export const requestTitleSearch = async (caseId?: string, title?: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_ParentIndexForTitleSearch?TitleSearchCaseID=${caseId}&Title=${title}`, {
            headers: {
                caseid: caseId || '',
                pzinskey: caseId || ''
            }
        });
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestTitleSearchSerial = async (parentId?: string, title?: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_SerialIndexByParentID?ParentID=${parentId}`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestTitleSearchFeature = async (parentId?: string, title?: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_FeatureIndexByParentID?ParentID=${parentId}`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestLatestVG = async () => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_LatestVideoGameDecisions?PublishStatus=Released`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestTitleSearchVG = async (title?: string, rating?: string, platform?: string) => {
    try {
        const response = await axiosServicesV1.get(
            `/data_iris/D_VGClassificationDBSearch?Title=${title}&Rating=${rating}&Platform=${platform}&PublishStatus=Released`
        );
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const requestTitleSearchFilms = async (title?: string, caseID?: string) => {
    try {
        const response = await axiosServicesV1.get(`/data_iris/D_ChildIndexTitleSearch?Title=${title}&Case ID=${caseID}&ForFEP=true`, {
            headers: {
                caseid: caseID || '',
                pzinskey: caseID || ''
            }
        });
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const getMediaLibraryByCaseId = async (id: string) => {
    try {
        const response = await axiosServicesV1.get(`${endpoint.GET_MEDIA_LIBRARY}${id}`, {
            headers: {
                caseid: id,
                pzinskey: id
            }
        });
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const requestInvoiceByType = async (caseTypeID: string, invoiceType: string) => {
    try {
        const response = await axiosServicesV1.get(`${endpoint.DATA}/D_GetInvoiceByType?CaseID=${caseTypeID}&InvoiceType=${invoiceType}`, {
            headers: {
                caseid: caseTypeID,
                pzinskey: caseTypeID
            }
        });
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const getActiveDistributionLicenceByUen = async (uen?: string) => {
    const decryptedUen = await decryptId(uen || '', 'decrypt');

    if (!uen) return {};

    try {
        const response = await axiosServicesV1.get(`${endpoint.DATA}/D_GetActiveDistributionLicenceByUen?UEN=${decryptedUen}`);
        return response.data.pxResults;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const getInvoiceDetails = async (invoiceKey?: string, parentPzinsKey?: string) => {
    if (!invoiceKey) return {};

    try {
        const { data: invoiceDetails } = await axiosServicesV1.get(`${endpoint.CASES}/${invoiceKey}`, {
            headers: {
                caseid: parentPzinsKey || invoiceKey,
                pzinskey: parentPzinsKey || invoiceKey
            }
        });
        return invoiceDetails;
    } catch (error) {
        console.error(error);
        return {};
    }
};
